import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  fetchServices,
  fetchServicesAll,
  createService,
  updateService,
  deleteService,
  fetchServiceByClient,
} from "./thunks";
import { Service } from "@/types";
import { toast } from "react-toastify";

interface PaginatedServices {
  data: Service[];
  total?: number;
  currentPage?: number;
  perPage?: number;
}

interface ServicesState {
  services: PaginatedServices;
  servicesAll: Service[]; // Lista de todos os serviços sem paginação
  servicesByClient: PaginatedServices;
  loading: boolean;
  loadingAll: boolean; // Loading específico para servicesAll
  error: string | null;
  fetched: boolean;
}

const initialState: ServicesState = {
  services: { data: [] },
  servicesAll: [], // Lista de todos os serviços sem paginação
  servicesByClient: { data: [] },
  loading: false,
  loadingAll: false, // Loading específico para servicesAll
  error: null,
  fetched: false,
};

const ServiceSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    clearSuppliers: (state) => {
      state.services = { data: [] };
      state.loading = false;
      state.error = null;
      state.fetched = false;
    },
    clearServicesAll: (state) => {
      state.servicesAll = [];
      state.loadingAll = false;
    },
    resetFetched: (state) => {
      state.fetched = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchServices.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.fetched = true;
        const payload = action.payload;

        if (Array.isArray(payload.data)) {
          // Dados não paginados
          state.services = { data: payload.data };
        } else {
          // Dados paginados
          state.services = {
            data: payload.data.data,
            total: payload.data.total,
            currentPage: payload.data.current_page,
            perPage: payload.data.per_page,
          };
        }
      })
      .addCase(fetchServices.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
        state.fetched = false;
      })
      // Fetch Services All
      .addCase(fetchServicesAll.pending, (state) => {
        state.loadingAll = true;
        state.error = null;
      })
      .addCase(
        fetchServicesAll.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loadingAll = false;
          const payload = action.payload;
          // Como é without_pagination, sempre será um array direto
          state.servicesAll = payload.data || [];
        },
      )
      .addCase(fetchServicesAll.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loadingAll = false;
      })
      .addCase(fetchServiceByClient.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchServiceByClient.fulfilled,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          const payload = action.payload;

          if (Array.isArray(payload.data)) {
            // Dados não paginados
            state.servicesByClient = { data: payload.data };
          } else {
            // Dados paginados
            state.servicesByClient = {
              data: payload.data.data,
              total: payload.data.total,
              currentPage: payload.data.current_page,
              perPage: payload.data.per_page,
            };
          }
        },
      )
      .addCase(fetchServiceByClient.rejected, (state, action) => {
        state.error = (action.payload as { error: string }).error;
        state.loading = false;
      })
      // Create
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.services.data.push(action.payload.data);
        // Também adicionar ao servicesAll se existir
        state.servicesAll.push(action.payload.data);
        toast.success("Serviço cadastrado com sucesso!");
      })
      .addCase(createService.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível cadastrar o serviço, tente novamente!",
        );
      })

      // Update
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        const index = state.services.data.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (index !== -1) {
          state.services.data[index] = action.payload.data;
        }
        // Também atualizar no servicesAll se existir
        const indexAll = state.servicesAll.findIndex(
          (s) => s.id == action.payload.data.id,
        );
        if (indexAll !== -1) {
          state.servicesAll[indexAll] = action.payload.data;
        }
        toast.success("Serviço atualizado com sucesso!");
      })
      .addCase(updateService.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível atualizar o serviço, tente novamente!",
        );
      })

      // Delete
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action: PayloadAction<any>) => {
        state.loading = false;
        state.services.data = state.services.data.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        // Também remover do servicesAll
        state.servicesAll = state.servicesAll.filter(
          (s) => String(s.id) != action.payload.data.id,
        );
        toast.success("Serviço excluído com sucesso!");
      })
      .addCase(deleteService.rejected, (state, action) => {
        const payload = action.payload as { error: string };
        state.error = payload.error;
        state.loading = false;
        toast.error(
          payload.error ||
            "Não foi possível excluir o serviço, tente novamente!",
        );
      });
  },
});

export const { clearSuppliers, clearServicesAll, resetFetched } =
  ServiceSlice.actions;
export default ServiceSlice.reducer;
